import {
  freeResourcesSliderWrapper,
  leftArrow,
  rightArrow,
} from "@/components/free-resources/styles/free-resources-slider";
import freeResourcesData from "@/i18n/json/free-resources/data.json";
import Slider from "react-slick";
import { LazyLoadComponent } from "react-lazy-load-image-component";
import React, { useEffect, useRef, useState } from "react";
import Arrow from "@/components/arrow";
import scss from "@/styles/pages/free-resources/slider.module.scss";
import { useTranslation } from "react-i18next";

import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { PromoEaster } from "../promo-easter/promo-easter";

const FreeResourcesSlider = () => {
  const [state, setState] = useState<number[]>([]);
  const slideContainer = useRef<HTMLDivElement>(null);
  const slidesLength = freeResourcesData.posters.length;
  const { t } = useTranslation();

  useEffect(() => {
    const slidesToShow =
      window?.innerWidth <= 480 ? 1 : window?.innerWidth <= 765 ? 3 : 5;
    setState(
      nextActiveSlideIndexes(slidesLength, 0, slidesToShow, "next", true)
    );
  }, []);

  const handleDirection = (currentIndex: number, nextIndex: number) => {
    switch (true) {
      case nextIndex - currentIndex < -1:
        return "next";
      case nextIndex - currentIndex > 1:
      case nextIndex < currentIndex:
        return "prev";
      default:
        return "next";
    }
  };

  const handleOnReInit = () => {
    const currentSlideIndex = Math.floor(state.length / 2);
    slideContainer.current?.querySelectorAll(".slick-slide").forEach((item) => {
      item.classList.add(...[scss["small-slide"], scss["slick-slide"]]);
      item.classList.remove(...[scss["medium-slide"], scss["big-slide"]]);
    });

    slideContainer.current
      ?.querySelector(".slick-track")
      ?.classList.add(scss["slick-track"]);
    slideContainer.current
      ?.querySelector(".slick-current")
      ?.classList.add(scss["slick-current"]);

    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex] + "']"
      )
      ?.classList.add(scss["big-slide"]);
    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex] + "']"
      )
      ?.classList.remove(scss["small-slide"]);

    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex + 1] + "']"
      )
      ?.classList.add(scss["medium-slide"]);
    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex + 1] + "']"
      )
      ?.classList.remove(scss["small-slide"]);

    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex - 1] + "']"
      )
      ?.classList.add(scss["medium-slide"]);
    slideContainer.current
      ?.querySelector(
        ".slick-slide[data-index='" + state[currentSlideIndex - 1] + "']"
      )
      ?.classList.remove(scss["small-slide"]);
  };

  const handleInitBeforeChange = (
    currentIndex: number,
    nextIndex: number,
    firstInit: boolean
  ) => {
    const direction = handleDirection(currentIndex, nextIndex);
    const slidesToShow =
      window?.innerWidth <= 480 ? 1 : window?.innerWidth <= 1280 ? 3 : 5;
    setState(
      nextActiveSlideIndexes(
        slidesLength,
        currentIndex ? currentIndex : 0,
        slidesToShow,
        direction,
        firstInit
      )
    );
  };

  const nextActiveSlideIndexes = (
    slidesLength: number,
    currentSlide: number,
    slidesToShow: number,
    direction: string,
    firstInit: boolean
  ) => {
    // Adjust the slides to show to how slick does it
    slidesToShow =
      (slidesToShow - 1) % 2 === 0 ? slidesToShow : slidesToShow + 1;

    const indexes = [];

    if (direction == "next") {
      currentSlide = currentSlide === slidesLength - 1 ? -1 : currentSlide;

      const sideSlides = (slidesToShow - 1) / 2; // setting the amount of slides that will be beside the center slide
      let slideStartIndex = currentSlide - sideSlides; // Setting the start index from a left to right direction Example:[(-2)-> start ,-1,0,1,2]

      // Creating the array
      for (let i = 0; i < slidesToShow; i++) {
        indexes.push(firstInit ? slideStartIndex : slideStartIndex + 1);
        slideStartIndex++;
      }
    } else if (direction == "prev") {
      currentSlide = currentSlide === 0 ? slidesLength - 1 : currentSlide - 1;

      const sideSlides = (slidesToShow - 1) / 2;
      let slideStartIndex = currentSlide - sideSlides - 1;

      for (let i = 0; i < slidesToShow; i++) {
        indexes.push(slideStartIndex + 1);
        slideStartIndex++;
      }
    }

    return indexes;
  };

  const CustomArrow: React.FC<{
    direction: "left" | "right";
    onClick?: () => void;
  }> = ({ direction, onClick }) => {
    return (
      <button
        type={"button"}
        className={`${
          direction === "right" ? leftArrow.join(" ") : rightArrow.join(" ")
        }`}
        onClick={onClick}
      >
        <Arrow
          dir={direction}
          color={"border-secondary"}
          size={2}
          scale={"scale-150"}
        />
      </button>
    );
  };

  const sliderSettings = {
    centerMode: true,
    adaptiveHeight: true,
    variableWidth: true,
    infinite: true,
    slidesToShow: 5,
    draggable: false,
    arrows: true,
    nextArrow: <CustomArrow direction="left" />,
    prevArrow: <CustomArrow direction="right" />,
    beforeChange: (currentIndex: number, nextIndex: number) => {
      handleInitBeforeChange(currentIndex, nextIndex, false);
    },
    onReInit: () => {
      handleOnReInit();
    },
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          centerPadding: "40px",
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 480,
        settings: {
          centerPadding: "40px",
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <div className={freeResourcesSliderWrapper} ref={slideContainer}>
      <LazyLoadComponent>
        <Slider
          {...sliderSettings}
          className={scss["clinic-poster-slider-wrapper"]}
        >
          {freeResourcesData.posters.map((_, index, posters) => {
            const posterId = posters.length - 1 - index;
            return (
              <div key={posterId} className={`text-center w-full h-full`}>
                <img
                  src={posters[posterId]["cover-image"]}
                  alt={t(`free-resources.posters.${posterId}.seo.alt`)}
                  title={t(`free-resources.posters.${posterId}.seo.title`)}
                />
                <p>{t(`free-resources.posters.${posterId}.title`)}</p>
                <a href={posters[posterId]["pdf-document"]} target="_blank">
                  <FontAwesomeIcon
                    className={"mr-2 mb-[2px]"}
                    size={"lg"}
                    color={"rgb(128,222,234)"}
                    icon={faDownload as IconProp}
                  />
                  <span>Download</span>
                </a>
              </div>
            );
          })}
        </Slider>
      </LazyLoadComponent>
    </div>
  );
};

export default FreeResourcesSlider;
